import { IAPIRoutes } from "../../../../config";

export type IUserRepository = ReturnType<typeof UserRepositoryFactory>;

export const UserRepositoryFactory = ({
  APIProvider,
  AuthService,
  APIRoutes,
}: {
  APIProvider: IAPIProvider;
  AuthService: IAuthService;
  APIRoutes: IAPIRoutes;
}) => {
  const { GET, PUT, POST, DELETE } = APIProvider;
  const { getAuthenticationToken } = AuthService;

  const getUserBetaFeatures: IGetUserBetaFeaturesAPI = async (payload) =>
    GET(
      APIRoutes.apiUserBetaFeature,
      payload?.clinic_id ? { clinic_id: payload.clinic_id } : {},
      {
        Authorization: `Bearer ${await getAuthenticationToken()}`,
      }
    );

  // Grants a user-scoped beta feature to the session user (auth header is
  // injected by apiProvider's getDefaultHeaders).
  const enableUserBetaFeature: IEnableUserBetaFeatureAPI = async (payload) =>
    POST(APIRoutes.apiAdminBetaFeature, {}, undefined, {
      queryStringParameters: {
        feature: payload.feature,
        target_type: "user",
        email: payload.email,
        ...(payload.description ? { description: payload.description } : {}),
      },
    });

  // Removes a user-scoped beta feature from the session user. The backend
  // takes a query string (e.g. `?feature=patient-page-v2`).
  const disableUserBetaFeature: IDisableUserBetaFeatureAPI = async (payload) =>
    DELETE(`${APIRoutes.apiUserBetaFeature}?feature=${payload.feature}`);

  const getPortalUserInfo: IGetPortalUserInfoAPI = async () =>
    GET(
      APIRoutes.apiGetPortalUserInfo,
      {},
      {
        Authorization: `Bearer ${await getAuthenticationToken()}`,
      }
    );

  const updateUserInfo: IUpdateUserInfoAPI = async (payload) =>
    PUT(APIRoutes.apiUpdateUserInfo, payload, {
      Authorization: `Bearer ${await getAuthenticationToken()}`,
    });

  const signupUser: ISignupUserAPI = async (payload) =>
    POST(APIRoutes.apiSignup, payload, {});

  const autoBedExit: IAutoBedExitAPI = async (payload) =>
    POST(APIRoutes.apiAutoBedExit, payload, {
      Authorization: `Bearer ${await getAuthenticationToken()}`,
    });

  const getNasaTlxPreference: IGetNasaTlxPreferenceAPI = async () =>
    GET(
      APIRoutes.apiNasaTlxPreference,
      {},
      {
        Authorization: `Bearer ${await getAuthenticationToken()}`,
      }
    );

  const submitNasaTlxPreference: ISubmitNasaTlxPreferenceAPI = async (
    payload
  ) =>
    POST(APIRoutes.apiNasaTlxPreference, payload, {
      Authorization: `Bearer ${await getAuthenticationToken()}`,
      "Content-Type": "application/json",
    });

  return {
    getUserBetaFeatures,
    enableUserBetaFeature,
    disableUserBetaFeature,
    getPortalUserInfo,
    updateUserInfo,
    signupUser,
    autoBedExit,
    getNasaTlxPreference,
    submitNasaTlxPreference,
  };
};
